import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import * as Sentry from "@sentry/angular";

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  constructor(private injector: Injector) { }

  handleError(error: any): void {
    const message = error.message ? error.message : error.toString();
    const stack = error.stack ? error.stack : error.toString();
    const location = this.injector.get(LocationStrategy);
    const url = location instanceof PathLocationStrategy ? location.path() : '';

    // 1. 청크 로딩 실패 특별 처리 (Webpack + esbuild 패턴 모두 지원)
    const chunkFailedMessage = /Loading chunk [\d]+ failed|Failed to fetch dynamically imported module|Importing a module script failed/;
    if (chunkFailedMessage.test(message)) {
      this.onNewAppDetected(error);
      return;  // Sentry로 전송 안 함
    }

    // 2. HttpErrorResponse를 구조화된 Error 객체로 변환 (HTTP Response 객체 직접 캡처 방지)
    let sentryError = error;
    if (error instanceof HttpErrorResponse) {
      // HttpErrorInterceptor와 동일한 정책: 0(네트워크 차단)/401/403/409/412는 정상 동작으로 간주하여 Sentry 전송 제외
      if ([0, 401, 403, 409, 412].includes(error.status)) {
        console.error(message, stack);
        return;
      }
      const httpError = new Error(`HTTP ${error.status} ${error.statusText}: ${error.url}`);
      httpError.name = 'HttpErrorResponse';
      sentryError = httpError;
    }

    // 3. Sentry로 에러 전송 (컨텍스트 포함)
    Sentry.captureException(sentryError, {
      tags: {
        error_handler: 'global',
      },
      contexts: {
        application: {
          url,
          message,
        },
      },
    });

    // 4. 콘솔에 에러 출력 (Sentry로 이미 전송되었으므로 re-throw 하지 않음)
    // re-throw 시 Zone.js가 같은 에러를 다시 잡아 Sentry에 중복 전송됨 (handled=no, <unknown>)
    console.error(message, stack);
  }

  onNewAppDetected(error: any): void {
    const chunkFileName = this.extractChunkFileName(error.message);
    const lastError = sessionStorage.getItem('last-chunk-error');
    const lastErrorTime = parseInt(sessionStorage.getItem('last-chunk-error-time') || '0', 10);
    const now = Date.now();

    // 같은 청크가 10초 이내에 다시 실패하면 무한 리로드 방지
    if (lastError === chunkFileName && (now - lastErrorTime) < 10000) {
      console.error('Multiple chunk failures detected. Stopping reload loop.');
      sessionStorage.removeItem('last-chunk-error');
      sessionStorage.removeItem('last-chunk-error-time');
      return;
    }

    sessionStorage.setItem('last-chunk-error', chunkFileName);
    sessionStorage.setItem('last-chunk-error-time', now.toString());

    window.location.reload();
  }

  extractChunkFileName(text: string) {
    // esbuild는 chunk-ABC1234.js 또는 파일명.hash.js 등 다양한 패턴을 가질 수 있음
    const regex = /https?:\/\/[^\s]+\.js/g;
    const matches = text.match(regex);
    return matches ? matches[0] : 'unknown-chunk';
  }
}
